import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Loader } from "semantic-ui-react";
import { getInspectionProjectDetails, getInspectionProjectDocuments, getInspectionProjectInspections } from "../../api/BorrowerApi";
import { getMockInspectionProjectDetails, getMockInspectionProjectDocuments, getMockInspectionProjectInspections } from "../../api/mock/MockBorrowerApi";
import { handleErrorResponse } from "../../Helpers";
import { BorrowerConstants } from "../constants/BorrowerConstants";
import { FeatureFlags } from "../constants/LocalServerConstants";
import { IErrorResponse, IProxyUser } from "../interfaces/Global";
import BCDashboard from "../ui/BCDashboard";
import BCMessage from "../ui/BCMessage";
import BCTabMenuBar from "../ui/BCTabMenuBar";
import BWWorkInspectionInteriorTable from "./BWWorkInspectionInteriorTable";

interface IBWWorkInspectionProject {
    isGreaterThanMobile: boolean;
    isGreaterThanTablet: boolean;
    isSidebar: boolean;
    isMessage: boolean;
    setIsMessage: React.Dispatch<React.SetStateAction<boolean>>;
    errorResponse: IErrorResponse | undefined; 
    setErrorResponse: React.Dispatch<React.SetStateAction<IErrorResponse | undefined>>; 
    projectId: string,
    projectProps: any,
    user: any; 
    proxyUser: IProxyUser | undefined;
}

const BWWorkInspectionProject = ({
    isGreaterThanMobile,
    isGreaterThanTablet,
    isSidebar,
    isMessage,
    setIsMessage,
    errorResponse,
    setErrorResponse,
    projectId,
    projectProps,
    user,
    proxyUser,
}  : IBWWorkInspectionProject) => {
    const [isLoading, setIsLoading] = useState(true as boolean);
    const [isFirstLoad, setIsFirstLoad] = useState(true as boolean);
    const [isInteriorCardLoading, setIsInteriorCardLoading] = useState(false as boolean);
    const [activeTab, setActiveTab] = useState(BorrowerConstants.inspectionInteriorCardTabs.Details.name.toLocaleUpperCase());
    const [projectDetails, setProjectDetails] = useState([] as any[]);
    const [projectDocuments, setProjectDocuments] = useState([] as any[]);
    const [projectInspections, setProjectInspections] = useState([] as any[]);

    const history = useHistory();
    const handleError = useCallback(() => {
        history.push("/error");
    }, [history]);

    const handleActiveTabAsync = async (newActiveTab) => {
        setIsInteriorCardLoading(true);
        setActiveTab(newActiveTab);
        setIsInteriorCardLoading(false);
    };

    useEffect(() => {
        const getInspectionProjectDetailsAsync = async (projectId) => {
            try {
                const response = FeatureFlags.IS_MOCK ? await getMockInspectionProjectDetails() : await getInspectionProjectDetails(projectId, proxyUser && proxyUser.id);
                if (response && response.ok) {
                    setIsMessage(false);
                    const workInspectionProject = await response.json();
                    setProjectDetails(workInspectionProject);
                }
                else {
                    await handleErrorResponse(response, setIsMessage, setErrorResponse);
                }
            }
            catch (error) {
                handleError();
            }
            setIsLoading(false);
        };

        const getInspectionProjectDocumentsAsync = async (projectId) => {
          try {
              const response = FeatureFlags.IS_MOCK ? await getMockInspectionProjectDocuments() : await getInspectionProjectDocuments(projectId, proxyUser && proxyUser.id);
              if (response && response.ok) {
                  setIsMessage(false);
                  const projectDocuments = await response.json();
                  setProjectDocuments(projectDocuments);
              }
              else {
                  await handleErrorResponse(response, setIsMessage, setErrorResponse);
              }
          }
          catch (error) {
              handleError();
          }
          setIsLoading(false);
        };

        const getInspectionProjectInspectionsAsync = async (projectId) => {
          try {
              const response = FeatureFlags.IS_MOCK ? await getMockInspectionProjectInspections() : await getInspectionProjectInspections(projectId, proxyUser && proxyUser.id);
              if (response && response.ok) {
                  setIsMessage(false);
                  const projectInspections = await response.json();
                  setProjectInspections(projectInspections);
              }
              else {
                  await handleErrorResponse(response, setIsMessage, setErrorResponse);
              }
          }
          catch (error) {
              handleError();
          }
          setIsLoading(false);
        };

        if (isFirstLoad) {
            getInspectionProjectDetailsAsync(projectId);
            getInspectionProjectDocumentsAsync(projectId);
            getInspectionProjectInspectionsAsync(projectId);
        }
    }, [user, proxyUser, setIsMessage, setErrorResponse, setIsLoading, handleError]);

    useEffect(() => {
        setIsFirstLoad(false);
    }, []);

    const LoaderLayout = [
        [
          {
            size: 16,
            content: <Loader active inline="centered" size={"big"} />
          }
        ]
    ];
    
    const ProjectDetailsLayout = [
        [
            {
                title: "Details",
                size: 16,
                content: <BWWorkInspectionInteriorTable 
                    isGreaterThanMobile={isGreaterThanMobile}
                    interiorTableData={projectDetails}
                    dataRules={[{ field: "Nwccproject Id", rule: "hide" }, { field: "Id", rule: "hide" }]}
                    interiorTableSortable={["Category", "Line Number", "Budget Amount"]} 
                    formatRules={[{field: "loanBudgetSubCategory", action: "rename", name: "Sub Category"}]}
                    emptyStateIcon="clipboard"
                    emptyStateText="Looks like there's no detail information for this project."
                />
            }
        ]
    ];

    const ProjectDocumentsLayout = [
        [
            {
                title: "Documents",
                size: 16,
                content: <BWWorkInspectionInteriorTable 
                    isGreaterThanMobile={isGreaterThanMobile}
                    interiorTableData={projectDocuments}
                    dataRules={[{ field: "Nwccproject Id", rule: "hide" }, { field: "Document Log Id", rule: "hide" }, { field: "Id", rule: "hide" }]}
                    interiorTableSortable={["Document File Name", "Created On", "Created By"]} 
                    emptyStateIcon="file"
                    emptyStateText="Looks like there's no document uploaded for this project."
                />
            }
        ]
    ];

    const ProjectInspectionLayout = [
        [
            {
                title: "Inspections",
                size: 16,
                content: <BWWorkInspectionInteriorTable 
                    isGreaterThanMobile={isGreaterThanMobile}
                    interiorTableData={projectInspections}
                    dataRules={[
                      { field: "Work Inspection Company Organization Id", rule: "hide" }, 
                      { field: "Nwccproject Id", rule: "hide" }, 
                      { field: "Allow Edit Id", rule: "hide" }, 
                      { field: "Loan Draw Status Name Id", rule: "hide" }, 
                      { field: "Id", rule: "hide" }
                    ]}
                    formatRules={[{field: "tcname", action: "rename", name: "TC Name"}, {field: "tcphoneNumber", action: "rename", name: "TC Phone Number"}]}
                    interiorTableSortable={[
                        "Loan Draw", 
                        "Inspection No", 
                        "Inspection Status", 
                        "Inspected On", 
                        "Completed On", 
                        "Created On",
                        "Updated On",
                        "Project Number",
                        "Property Type",
                        "Loan Number",
                        "City",
                        "Project Name",
                        "Project No",
                        "Requested Date",
                        "Borrower",
                        "Builder",
                        "Inspection Company"
                    ]}
                    emptyStateIcon="list alternate outline"
                    emptyStateText="Looks like there's no inspection information for this project."
                />
            }
        ]
    ];

    return (
        <div className={(isGreaterThanTablet && isSidebar) ? "landing-page hide-sidebar" : "landing-page sidebar"}>
            {isLoading ? <Loader active inline="centered" size="big" /> : <React.Fragment>
                <BCMessage
                    animation={"fly down"}
                    duration={750}
                    visible={isMessage}
                    transitionOnMount
                    styles="full-width"
                    onDismiss={() => setIsMessage(false)}
                    errorResponse={errorResponse}
                />
                <BCTabMenuBar
                    title="Work Inspection Project"
                    subtitleLists={
                      [
                        [
                          // { title: "Loan", detail: `${projectProps.loanNumber}`},
                          { title: "Loan", detail: "1"},
                          // { title: "Project No", detail: `${projectProps.projectNumber}`},
                          // { title: "Loan Property", detail: `${projectProps.loanProperty}`}
                        ]
                      ]
                    }
                    tabs={Object.values(BorrowerConstants.inspectionInteriorCardTabs)}
                    activeTab={activeTab}
                    setActiveTab={handleActiveTabAsync}
                    isGreaterThanMobile={isGreaterThanMobile}
                />
                <BCDashboard
                    DashboardLayout={isInteriorCardLoading ? LoaderLayout : 
                        activeTab === BorrowerConstants.inspectionInteriorCardTabs.Details.name.toLocaleUpperCase() ? ProjectDetailsLayout :
                        activeTab === BorrowerConstants.inspectionInteriorCardTabs.Documents.name.toLocaleUpperCase() ? ProjectDocumentsLayout :
                        activeTab === BorrowerConstants.inspectionInteriorCardTabs.Inspections.name.toLocaleUpperCase() ? ProjectInspectionLayout : []}
                    isGreaterThanMobile={isGreaterThanMobile}
                />
              {FeatureFlags.IS_DEBUG && <React.Fragment>
                <button onClick={() => console.log(projectDetails)}>projectDetails</button>
                <button onClick={() => console.log(projectDocuments)}>projectDocuments</button>
                <button onClick={() => console.log(projectInspections)}>projectInspections</button>
                <button onClick={() => console.log(projectProps)}>projectProps</button>
              </React.Fragment>}
            </React.Fragment>
            }
        </div>
    );
};

export default BWWorkInspectionProject;